import { vec3 } from "gl-matrix";
import { useCallback, useEffect, useRef } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useMarkerRenderer } from "contexts/markerSvgRenderer";
import type { AnchoredSvgData } from "features/engine2D/svgRenderer/types";
import { isInsideViewport } from "features/engine2D/utils";

import {
    selectAllCollections,
    selectCanDragSelectedMarker,
    selectCreationCollectionId,
    selectCreationPreviewPosition,
    selectEditPreviewPosition,
    selectHoveredMarker,
    selectIsCreatingMarker,
    selectIsEditingMarkerLocation,
    selectSelectedMarker,
} from "./markersSlice";
import type { MarkerIcon, MarkerMarkupBadge } from "./types";
import { useMarkerInteraction } from "./useMarkerInteraction";
import { addIconToSvgData, createCompositeId, darkenColor } from "./utils";

const DEFAULT_ICON_SIZE = 32;
const DEFAULT_STROKE_COLOR = "#ffffff";
const DEFAULT_SELECTED_STROKE_WIDTH = 2;
const DEFAULT_HOVER_STROKE_WIDTH = 2;
const UNSELECTED_DIMMING_OPACITY_RATIO = 0.7;
const DEFAULT_MARKER_COLOR = "#2196F3";
const DEFAULT_STROKE_WIDTH = 1;
const SELECTED_SIZE_RATIO = 1.2;
const HOVERED_SIZE_RATIO = 1.2;
const OPAQUE = 1;

// 3D Perspective scaling constants
const MAX_MARKER_FACTOR = 1; // Scale factor for very near markers
const MIN_MARKER_FACTOR = 0.2; // Scale factor for very far markers

/**
 * Calculate reference distances based on scene bounding sphere.
 * This automatically adapts to the scene size.
 * @param boundingSphereRadius - Radius of the scene's bounding sphere
 * @returns Object with near and far reference distances
 */
function calculateReferenceDistances(boundingSphereRadius: number | undefined): {
    near: number;
    far: number;
} {
    if (!boundingSphereRadius || boundingSphereRadius === 0) {
        // Fallback for unknown scene size - conservative defaults that work better for small scenes
        return { near: 1, far: 20 };
    }

    // Scale reference distances proportionally to scene size
    // Near: 5% of scene radius (close-up perspective kicks in)
    // Far: 100% of scene radius (max distance for perspective effect)
    // This ensures the scaling works for tiny scenes (cm) to huge scenes (km)
    const near = Math.max(1, boundingSphereRadius * 0.05);
    const far = Math.max(near * 2, boundingSphereRadius);

    return { near, far };
}

/**
 * Manages marker rendering via SvgRenderer
 * Subscribes to Redux state and updates renderer when markers change
 */
export function MarkerRendererManager() {
    const {
        state: { view, internalEventEmitter, size },
    } = useExplorerGlobals();
    const markerRendererRef = useMarkerRenderer();
    const collections = useAppSelector(selectAllCollections);
    const hoveredMarker = useAppSelector(selectHoveredMarker);
    const selectedMarker = useAppSelector(selectSelectedMarker);
    const canDragSelectedMarker = useAppSelector(selectCanDragSelectedMarker);
    const isCreatingMarker = useAppSelector(selectIsCreatingMarker);
    const creationPreviewPosition = useAppSelector(selectCreationPreviewPosition);
    const creationCollectionId = useAppSelector(selectCreationCollectionId);
    const isEditingMarkerLocation = useAppSelector(selectIsEditingMarkerLocation);
    const editPreviewPosition = useAppSelector(selectEditPreviewPosition);

    // Get interaction event handlers from the interaction hook
    const { handleMarkerDown, handleMarkerHover, handleMarkerOut, reconcileHoverAfterRedraw } = useMarkerInteraction();

    // Cache reference distances to avoid recalculating on every frame
    const referenceDistancesRef = useRef<{ near: number; far: number } | null>(null);
    const prevBoundingSphereRadiusRef = useRef<number | undefined>(undefined);
    const hasRenderedContentRef = useRef(false);
    const rafIdRef = useRef<number | null>(null);
    const pendingSizeRef = useRef<{ width: number; height: number } | undefined>(undefined);
    const needsHoverReconcileRef = useRef(false);
    const isInMotionRef = useRef(false);

    // Function to build marker data with perspective scaling based on camera distance
    const buildAndDrawMarkers = useCallback(
        (sizeOverride?: { width: number; height: number }) => {
            const renderer = markerRendererRef.current;

            if (!renderer || !view) {
                return;
            }

            if (Object.keys(collections).length === 0) {
                if (hasRenderedContentRef.current) {
                    renderer.clear();
                    hasRenderedContentRef.current = false;
                }
                return;
            }

            // Calculate reference distances based on scene size (with caching)
            const sceneBoundingSphere = view.renderState.scene?.config.boundingSphere;
            const currentRadius = sceneBoundingSphere?.radius;

            // Only recalculate reference distances if bounding sphere radius changes
            if (currentRadius !== prevBoundingSphereRadiusRef.current || !referenceDistancesRef.current) {
                referenceDistancesRef.current = calculateReferenceDistances(currentRadius);
                prevBoundingSphereRadiusRef.current = currentRadius;
            }

            const REFERENCE_NEAR_DISTANCE = referenceDistancesRef.current.near;
            const REFERENCE_FAR_DISTANCE = referenceDistancesRef.current.far;

            renderer.setRenderState({
                camera: view.renderState.camera,
                size: sizeOverride ?? size,
            });

            // Clear previous markers before rendering new ones
            renderer.clear();

            // It should accept an array of paths but that is WIP. Just using ellipses for now.
            const data: AnchoredSvgData = {
                ellipses: [],
                rects: [],
                svgContents: [],
            };

            // Get camera position once for all distance calculations
            const cameraPosition = view.renderState.camera.position;
            let toScreenSpace: ReturnType<typeof view.convert.worldSpaceToScreenSpaceConverter> | undefined;
            try {
                toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false });
            } catch (error) {
                console.warn("Failed to get world space to screen space converter. Error: ", error);
            }
            const viewportSize = sizeOverride ?? size;
            const canCullByViewport = Boolean(
                toScreenSpace &&
                viewportSize &&
                Number.isFinite(viewportSize.width) &&
                Number.isFinite(viewportSize.height),
            );

            // Process all visible markers in a single pass - calculate distance and render immediately
            for (const collection of Object.values(collections)) {
                if (!collection.visible) {
                    continue;
                }
                for (const marker of Object.values(collection.markers)) {
                    const isHovered =
                        hoveredMarker !== null &&
                        hoveredMarker.collectionId === collection.id &&
                        hoveredMarker.markerId === marker.id;
                    const isSelected =
                        selectedMarker !== null &&
                        selectedMarker.collectionId === collection.id &&
                        selectedMarker.markerId === marker.id;
                    const hasSelection = selectedMarker !== null;
                    const isBeingEdited =
                        isEditingMarkerLocation &&
                        selectedMarker !== null &&
                        selectedMarker.collectionId === collection.id &&
                        selectedMarker.markerId === marker.id;

                    // Determine which position to render marker at
                    let renderPosition = marker.position;
                    if (isBeingEdited && editPreviewPosition) {
                        renderPosition = editPreviewPosition;
                    }

                    const mustAlwaysRender = isSelected || isHovered || isBeingEdited;
                    if (!mustAlwaysRender && canCullByViewport && toScreenSpace && viewportSize) {
                        const screenPoint = toScreenSpace(renderPosition);
                        if (!isInsideViewport(screenPoint, viewportSize)) {
                            continue;
                        }
                    }

                    // Calculate distance from camera to marker
                    const distance = vec3.distance(cameraPosition, renderPosition);

                    // Get config from collection
                    const config = collection.config;

                    const icon: MarkerIcon | null = (config?.getPinIcon?.(marker) as MarkerIcon | null) ?? null;

                    // Get base size from icon.size, or use default
                    const baseIconSize = icon && "size" in icon && icon.size ? icon.size : DEFAULT_ICON_SIZE;

                    // Calculate perspective scale factor using absolute distance thresholds
                    let perspectiveFactor;

                    if (distance <= REFERENCE_NEAR_DISTANCE) {
                        // Very close markers get maximum scaling
                        perspectiveFactor = MAX_MARKER_FACTOR;
                    } else if (distance >= REFERENCE_FAR_DISTANCE) {
                        // Very far markers get minimum scaling
                        perspectiveFactor = MIN_MARKER_FACTOR;
                    } else {
                        // Markers in between get smooth interpolation based on absolute distance
                        // Use pre-calculated distanceRange and factorRange to avoid redundant operations
                        const distanceRange = REFERENCE_FAR_DISTANCE - REFERENCE_NEAR_DISTANCE;
                        if (distanceRange <= 0) {
                            // Fallback to a safe value when range is zero or invalid to avoid division by zero
                            perspectiveFactor = MIN_MARKER_FACTOR;
                        } else {
                            let normalizedDistance = (distance - REFERENCE_NEAR_DISTANCE) / distanceRange;
                            // Clamp to [0, 1] for robustness; for valid inputs this is a no-op
                            if (normalizedDistance < 0) {
                                normalizedDistance = 0;
                            } else if (normalizedDistance > 1) {
                                normalizedDistance = 1;
                            }
                            const factorRange = MAX_MARKER_FACTOR - MIN_MARKER_FACTOR;
                            perspectiveFactor = MAX_MARKER_FACTOR - normalizedDistance * factorRange;
                        }
                    }

                    // Apply perspective scaling to base size
                    let iconSize = Math.round(baseIconSize * perspectiveFactor);

                    // Get base stroke color from getPinColor
                    let strokeColor = config?.getPinColor?.(marker) || DEFAULT_STROKE_COLOR;
                    let strokeWidth = DEFAULT_STROKE_WIDTH;

                    // Get base fill color from icon.color, or compute from stroke color
                    let fillColor =
                        icon && "color" in icon && icon.color
                            ? icon.color
                            : darkenColor(strokeColor, strokeColor === "#ffffff" ? 1.0 : 0.7);

                    // Apply state-specific colors
                    if (isSelected) {
                        const selectedColor = config?.getSelectedColor?.(marker);
                        if (selectedColor) {
                            strokeColor = selectedColor;
                            fillColor = darkenColor(selectedColor, 0.7);
                        }
                        // Use config size or scale perspective-adjusted size by 1.2x for selected state
                        iconSize =
                            config?.getSelectedSize?.(marker) ||
                            Math.round(baseIconSize * perspectiveFactor * SELECTED_SIZE_RATIO);
                        strokeWidth = DEFAULT_SELECTED_STROKE_WIDTH;
                    } else if (isHovered) {
                        const hoverColor = config?.getHoverColor?.(marker);
                        if (hoverColor) {
                            strokeColor = hoverColor;
                            fillColor = darkenColor(hoverColor, 0.7);
                        }
                        // Use config size or scale perspective-adjusted size by 1.2x for hover state
                        iconSize =
                            config?.getHoverSize?.(marker) ||
                            Math.round(baseIconSize * perspectiveFactor * HOVERED_SIZE_RATIO);
                        strokeWidth = DEFAULT_HOVER_STROKE_WIDTH;
                    }

                    // Opacity: Dim non-selected markers when something is selected (across all collections)
                    let opacity = config?.getPinOpacity?.(marker) || OPAQUE;
                    if ((hasSelection && !isSelected) || (isCreatingMarker && creationPreviewPosition)) {
                        opacity = opacity * UNSELECTED_DIMMING_OPACITY_RATIO; // Reduce to 70% when not selected
                    }
                    const markupBadge: MarkerMarkupBadge = collection.config.getMarkupBadge?.(marker) || false;

                    // Cursor feedback follows interaction state: grabbing while editing, move when selected, pointer otherwise
                    let markerCursor = "pointer";
                    if (isSelected && canDragSelectedMarker) {
                        markerCursor = "move";
                    }
                    if (isBeingEdited) {
                        markerCursor = "grabbing";
                    }

                    const shouldUseLowLod = isInMotionRef.current === true && !isSelected && !isHovered;
                    const effectiveMarker: MarkerIcon | null = shouldUseLowLod ? { shape: "circle" } : icon;
                    // Render the marker immediately at the determined position
                    addIconToSvgData(data, effectiveMarker, renderPosition, {
                        color: fillColor || DEFAULT_MARKER_COLOR,
                        size: iconSize,
                        opacity: opacity,
                        stroke: strokeColor,
                        strokeWidth: strokeWidth,
                        useMarkupBadge: shouldUseLowLod ? false : markupBadge,
                        alignToPinHead: shouldUseLowLod,
                        cursor: markerCursor,
                        id: createCompositeId(collection.id, marker.id),
                        onPointerDown: handleMarkerDown,
                        onPointerEnter: handleMarkerHover,
                        onPointerOut: handleMarkerOut,
                    });
                }
            }

            if (isCreatingMarker && creationPreviewPosition && creationCollectionId) {
                const creationCollection = collections[creationCollectionId];
                const config = creationCollection?.config;
                // No marker is created yet, so we need to solve this to get the icon styling.
                const icon: MarkerIcon | null = (config?.getPinIcon?.() as MarkerIcon | null) ?? null;
                const iconSize = icon && "size" in icon ? icon.size || DEFAULT_ICON_SIZE : DEFAULT_ICON_SIZE;
                addIconToSvgData(data, icon, creationPreviewPosition, {
                    color: darkenColor(DEFAULT_STROKE_COLOR, DEFAULT_STROKE_COLOR === "#ffffff" ? 1.0 : 0.7),
                    size: iconSize,
                    useMarkupBadge: false,
                    opacity: OPAQUE,
                    stroke: DEFAULT_STROKE_COLOR,
                    strokeWidth: DEFAULT_STROKE_WIDTH,
                });
            }

            renderer.drawAnchored(data);
            hasRenderedContentRef.current = true;
        },
        [
            collections,
            hoveredMarker,
            selectedMarker,
            canDragSelectedMarker,
            creationPreviewPosition,
            creationCollectionId,
            isCreatingMarker,
            isEditingMarkerLocation,
            editPreviewPosition,
            markerRendererRef,
            handleMarkerDown,
            handleMarkerHover,
            handleMarkerOut,
            size,
            view,
        ],
    );

    const scheduleRedraw = useCallback(
        (options?: { sizeOverride?: { width: number; height: number }; reconcileHover?: boolean }) => {
            if (options?.sizeOverride) {
                pendingSizeRef.current = options.sizeOverride;
            }
            if (options?.reconcileHover) {
                needsHoverReconcileRef.current = true;
            }
            if (rafIdRef.current !== null) {
                return;
            }

            rafIdRef.current = requestAnimationFrame(() => {
                rafIdRef.current = null;

                const sizeOverride = pendingSizeRef.current;
                const shouldReconcileHover = needsHoverReconcileRef.current;

                pendingSizeRef.current = undefined;
                needsHoverReconcileRef.current = false;

                buildAndDrawMarkers(sizeOverride);
                if (shouldReconcileHover) {
                    reconcileHoverAfterRedraw();
                }
            });
        },
        [buildAndDrawMarkers, reconcileHoverAfterRedraw],
    );

    // Render markers when state changes and subscribe to camera movement events
    useEffect(() => {
        scheduleRedraw();

        if (!internalEventEmitter) {
            return () => {
                if (rafIdRef.current !== null) {
                    cancelAnimationFrame(rafIdRef.current);
                    rafIdRef.current = null;
                }
            };
        }

        // Subscribe to camera movement events for perspective scaling updates
        const unsubscribeCameraUpdated = internalEventEmitter.on("cameraUpdated", () => {
            isInMotionRef.current = true;
            scheduleRedraw({ reconcileHover: true });
        });

        const unsubscribeCameraSettled = internalEventEmitter.on("cameraSettled", () => {
            isInMotionRef.current = false;
            scheduleRedraw({ reconcileHover: true });
        });

        const unsubscribeResize = internalEventEmitter.on("appResize", (payload) => {
            scheduleRedraw({ sizeOverride: payload });
        });

        return () => {
            unsubscribeCameraUpdated();
            unsubscribeCameraSettled();
            unsubscribeResize();
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, [internalEventEmitter, scheduleRedraw]);

    return null; // Logic component, no UI
}
